import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { OUItem } from '@/api/types';
import { buildOuTree } from './ouTree';
import { useOuSelection } from './useOuSelection';
import { useOuCascadeNavigation } from './useOuCascadeNavigation';
import { getContrastTextColor } from './ouContrast';

export interface OuCascadeSelectProps {
  rootOu: OUItem;
  items: OUItem[];
  value: string[];
  onChange: (ids: string[]) => void;
  color: string;
  onCollapse: () => void;
  /**
   * Se dispara al desmarcar "Seleccionar todo" en el nivel raíz (no en niveles
   * inferiores). Un `value` vacío no distingue "sin personalizar (= toda la OU)"
   * de "sin nada seleccionado", así que desmarcar en la raíz no puede expresarse
   * como un `value` — el padre debe quitar la OU completa de su selección.
   */
  onRootDeselect: () => void;
}

export function OuCascadeSelect({ rootOu, items, value, onChange, color, onCollapse, onRootDeselect }: OuCascadeSelectProps) {
  const { t } = useTranslation();
  const allItems = useMemo(() => [rootOu, ...items], [rootOu, items]);
  const tree = buildOuTree(allItems);
  const root = tree[0]!;
  const { getState, toggle } = useOuSelection(allItems, value, onChange);
  const { breadcrumbPath, currentNode, visibleChildren, drillInto, goToLevel } = useOuCascadeNavigation(root);
  const isDarkText = getContrastTextColor(color) === 'dark';

  function handleBack(): void {
    if (breadcrumbPath.length > 1) {
      goToLevel(breadcrumbPath.length - 2);
    } else {
      onCollapse();
    }
  }

  function handleToggleCurrentLevel(): void {
    const isRootLevel = currentNode.ou.id === root.ou.id;
    const wasChecked = getState(currentNode.ou.id) === 'checked';
    if (isRootLevel && wasChecked) {
      onRootDeselect();
      return;
    }
    toggle(currentNode.ou.id);
  }

  function renderCheckboxRow(nodeId: string, label: string, onToggleOverride?: () => void): ReactNode {
    const state = getState(nodeId);
    return (
      <label className="ou-cascade-checkbox-label">
        <input
          type="checkbox"
          checked={state === 'checked'}
          ref={inputElement => {
            if (inputElement) inputElement.indeterminate = state === 'indeterminate';
          }}
          onChange={() => (onToggleOverride ? onToggleOverride() : toggle(nodeId))}
          aria-label={label}
        />
        <span>{label}</span>
      </label>
    );
  }

  return (
    <div className="ou-cascade-panel">
      <div
        className={`ou-cascade-header${isDarkText ? ' ou-cascade-header--dark-text' : ''}`}
        style={{ backgroundColor: color }}
      >
        <button type="button" className="ou-cascade-back" onClick={handleBack} aria-label={t('admin.ouBackToLevels')}>
          <i className="fas fa-chevron-left" aria-hidden="true" />
        </button>
        {breadcrumbPath.map((node, index) => (
          <span key={node.ou.id} className="ou-cascade-breadcrumb-segment">
            {index > 0 && <i className="fas fa-chevron-right ou-cascade-breadcrumb-sep" aria-hidden="true" />}
            {index === breadcrumbPath.length - 1 ? (
              <span className="ou-cascade-breadcrumb-current">{node.ou.name.toUpperCase()}</span>
            ) : (
              <button type="button" className="ou-cascade-breadcrumb-link" onClick={() => goToLevel(index)}>
                {node.ou.name}
              </button>
            )}
          </span>
        ))}
      </div>

      <div className="ou-cascade-item ou-cascade-all">
        {renderCheckboxRow(currentNode.ou.id, t('admin.ouSelectAllInLevel'), handleToggleCurrentLevel)}
      </div>
      <div className="ou-cascade-divider" />

      {visibleChildren.map(child => (
        <div key={child.ou.id} className="ou-cascade-item">
          {renderCheckboxRow(child.ou.id, child.ou.name)}
          {child.children.length > 0 && (
            <button
              type="button"
              className="ou-cascade-drill"
              onClick={() => drillInto(child.ou.id)}
              aria-label={`${t('admin.ouExpand')} ${child.ou.name}`}
            >
              <i className="fas fa-chevron-right ou-cascade-arrow" aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
